import { useRef } from 'react';

import { MtxButton } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import VIDEO_TUTORIAL_STATUS from '@/enums/videoTutorialStatus';
import useVideoTutorialActionFocus from '@/hooks/useVideoTutorialActionFocus';
import useVideoTutorialPlayer from '@/hooks/useVideoTutorialPlayer';

import styles from './styles.module.css';

import type { TutorielVideo } from '@/types/videoTutorials';

interface VideoTutorialProps {
  tutorial: TutorielVideo;
  title: string;
  autoStart?: boolean;
  onPlay?: (tutorial: TutorielVideo) => void;
}

const VideoTutorial = ({ tutorial, title, autoStart, onPlay }: VideoTutorialProps) => {
  const { t } = useTranslation();
  const { status, iframeKey, iframeSrc, iframeRef, play, retry, handleIframeLoad } = useVideoTutorialPlayer(tutorial, {
    autoStart,
    onPlay,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useVideoTutorialActionFocus(containerRef, status);

  return (
    <div ref={containerRef} className={styles.videoTutorial}>
      {status === VIDEO_TUTORIAL_STATUS.IDLE && (
        <button
          type="button"
          className={styles.playButton}
          onClick={play}
          aria-label={t('videoTutorials.playButtonLabel', { title })}
        >
          <i className="totem-icon totem-icon-play" aria-hidden="true" />
        </button>
      )}

      {status === VIDEO_TUTORIAL_STATUS.PLAYING && (
        <iframe
          key={iframeKey}
          ref={iframeRef}
          className={styles.iframe}
          src={iframeSrc}
          title={title}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={handleIframeLoad}
        />
      )}

      {status === VIDEO_TUTORIAL_STATUS.ENDED && (
        <div className={styles.overlay}>
          <MtxButton variant="primary" size="medium" onClick={play}>
            <div slot="start-icon" className={styles.buttonIcon}>
              <i className="totem-icon totem-icon-refresh" aria-hidden="true" />
            </div>
            {t('videoTutorials.replayButton')}
          </MtxButton>
        </div>
      )}

      {/* Région live toujours montée : un <output> (role status) inséré avec son texte n'est pas annoncé. */}
      <div className={status === VIDEO_TUTORIAL_STATUS.ERROR ? styles.overlay : undefined}>
        <output className={styles.errorMessage}>
          {status === VIDEO_TUTORIAL_STATUS.ERROR && t('videoTutorials.errorMessage')}
        </output>
        {status === VIDEO_TUTORIAL_STATUS.ERROR && (
          <MtxButton variant="primary" size="medium" onClick={retry}>
            {t('videoTutorials.retryButton')}
          </MtxButton>
        )}
      </div>
    </div>
  );
};

export default VideoTutorial;
